import React from 'react';
import {
  Box,
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { AgentCardProps } from './AgentCard';

interface ReviewTableProps {
  agents: AgentCardProps[];
  onApprove: (agent: AgentCardProps) => void;
  onReject: (agent: AgentCardProps) => void;
  onReview: (agent: AgentCardProps) => void;
}

const useStyles = makeStyles((theme: Theme) => {
  const isDarkMode = theme.palette.type === 'dark';

  return {
  wrapper: {
    background: theme.palette.background.paper,
    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#e5e5e5'}`,
    borderRadius: 8,
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: 'transparent',
  },
  cell: {
    padding: '10px 12px',
    borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.14)' : '#f0f0f0'}`,
    textAlign: 'left',
    fontSize: 13,
    color: theme.palette.text.secondary,
    background: 'transparent',
  },
  actions: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  emptyState: {
    background: theme.palette.background.paper,
    border: `1px dashed ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#d0d0d0'}`,
    borderRadius: 8,
    padding: 20,
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  lightBtn: {
    textTransform: 'none',
    color: theme.palette.text.secondary,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : undefined,
    background: isDarkMode ? 'rgba(255,255,255,0.04)' : undefined,
    '&:hover': {
      background: isDarkMode ? 'rgba(255,255,255,0.1)' : undefined,
    },
  },
  primaryBtn: {
    textTransform: 'none',
    background: '#003f2d',
    color: '#fff',
    '&:hover': {
      background: '#002d21',
    },
  },
  dangerBtn: {
    textTransform: 'none',
    background: '#b42318',
    color: '#fff',
    '&:hover': {
      background: '#8f1f14',
    },
  },
  row: {
    '&:hover': {
      background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#fafafa',
    },
  },
  headRow: {
    background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#fafafa',
  },
  headCell: {
    fontWeight: 600,
  },
};
});

export const ReviewTable: React.FC<ReviewTableProps> = ({ agents, onApprove, onReject, onReview }) => {
  const classes = useStyles();

  if (!agents.length) {
    return <Box className={classes.emptyState}>No items pending review.</Box>;
  }

  return (
    <TableContainer className={classes.wrapper}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.headRow}>
            <TableCell className={`${classes.cell} ${classes.headCell}`}>Agent</TableCell>
            <TableCell className={`${classes.cell} ${classes.headCell}`}>Owner</TableCell>
            <TableCell className={`${classes.cell} ${classes.headCell}`}>Type</TableCell>
            <TableCell className={`${classes.cell} ${classes.headCell}`}>Status</TableCell>
            <TableCell className={`${classes.cell} ${classes.headCell}`}>Updated</TableCell>
            <TableCell className={`${classes.cell} ${classes.headCell}`}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agents.map(agent => (
            <TableRow className={classes.row} key={agent.id || agent.title}>
              <TableCell className={classes.cell}>
                <Typography variant="body2">{agent.title}</Typography>
              </TableCell>
              <TableCell className={classes.cell}>{agent.owner}</TableCell>
              <TableCell className={classes.cell}>{agent.agentType}</TableCell>
              <TableCell className={classes.cell}>{agent.approvalStatus}</TableCell>
              <TableCell className={classes.cell}>{agent.updatedTime}</TableCell>
              <TableCell className={classes.cell}>
                <Box className={classes.actions}>
                  <Button variant="outlined" size="small" className={classes.lightBtn} onClick={() => onReview(agent)}>
                    Review
                  </Button>
                  <Button variant="contained" size="small" className={classes.primaryBtn} onClick={() => onApprove(agent)}>
                    Approve
                  </Button>
                  <Button variant="contained" size="small" className={classes.dangerBtn} onClick={() => onReject(agent)}>
                    Reject
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
